/**
 * PageResultPage 컴포넌트
 * 페이지 채점 결과 화면
 * - 결과 요약 (정답률, 점수)
 * - 문제별 결과 목록
 * - 액션 버튼 (다시 풀기, 다른 페이지, 홈으로)
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PageResult } from '../types/result';
import { getPageResult } from '../services/resultService';
import { ResultSummary } from '../components/result/ResultSummary';
import { ProblemResultList } from '../components/result/ProblemResultList';
import { PixelButton } from '../components/common/PixelButton';

/**
 * PageResultPage 컴포넌트
 * 페이지 채점 결과 화면
 */
export const PageResultPage: React.FC = () => {
  const { workbook, page } = useParams<{ workbook: string; page: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // location.state로 전달된 결과가 있으면 사용, 없으면 API 호출
  const [result, setResult] = useState<PageResult | null>(
    location.state?.result || null
  );
  const [loading, setLoading] = useState(!result);
  const [error, setError] = useState<string | null>(null);

  /**
   * 결과 조회 (location.state에 없을 때만)
   */
  useEffect(() => {
    if (!result && workbook && page) {
      const fetchResult = async () => {
        try {
          setLoading(true);
          const data = await getPageResult(workbook, parseInt(page));
          setResult(data);
        } catch (err: any) {
          console.error('결과 조회 실패:', err);
          setError(err.response?.data?.detail || '결과를 불러오는데 실패했습니다');
        } finally {
          setLoading(false);
        }
      };
      fetchResult();
    }
  }, [workbook, page, result]);

  /**
   * 다시 풀기 핸들러 (SolvePage로 이동)
   */
  const handleRetry = () => {
    navigate(`/workbooks/${workbook}/solve`);
  };

  /**
   * 다른 페이지 핸들러 (SolvePage로 이동)
   */
  const handleOtherPage = () => {
    navigate(`/workbooks/${workbook}/solve`);
  };

  /**
   * 홈으로 핸들러
   */
  const handleGoHome = () => {
    navigate('/');
  };

  // 로딩 상태
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="font-pixel text-lg text-[#9bbc0f]">로딩 중...</p>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-center">
          <p className="font-pixel text-lg text-red-400 mb-4">😢 {error}</p>
          <PixelButton variant="primary" size="medium" onClick={handleGoHome}>
            홈으로 돌아가기
          </PixelButton>
        </div>
      </div>
    );
  }

  // 결과가 없을 때
  if (!result) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="font-pixel text-lg text-gray-400">결과를 찾을 수 없습니다</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4">
      <div className="max-w-4xl mx-auto">
        {/* 결과 요약 */}
        <ResultSummary result={result} />

        {/* 문제별 결과 목록 */}
        {result.problems.length > 0 && (
          <div className="mt-4">
            <h3 className="font-pixel text-lg text-[#9bbc0f] mb-3">
              문제별 결과
            </h3>
            <ProblemResultList problems={result.problems} />
          </div>
        )}

        {/* 액션 버튼 */}
        <div className="mt-6 space-y-3">
          {/* 다시 풀기 버튼 - 재채점 구현 시 활성화 예정 */}
          {/* TODO: 재채점 허락 여부에 따라 활성화 */}
          {/* {result.wrong_problems > 0 && (
            <PixelButton
              variant="primary"
              size="medium"
              onClick={handleRetry}
              fullWidth
            >
              다시 풀기 (오답 {result.wrong_problems}문제)
            </PixelButton>
          )} */}

          {/* 하단 버튼 */}
          <div className="grid grid-cols-2 gap-3">
            <PixelButton
              variant="secondary"
              size="medium"
              onClick={handleOtherPage}
            >
              다른 페이지
            </PixelButton>
            <PixelButton
              variant="secondary"
              size="medium"
              onClick={handleGoHome}
            >
              홈으로
            </PixelButton>
          </div>
        </div>
      </div>
    </div>
  );
};
